/**
 * Thin promise wrapper over IndexedDB. Three object stores:
 *   roundups  — RoundupInvestment records keyed by id
 *   spends    — DetectedSpend records keyed by `${txHash}:${logIndex}`
 *   kv        — settings, scan state, wallet material, demo balances
 */
import type {
  AppSettings,
  CardTopUpState,
  DetectedSpend,
  GasBootstrapState,
  RoundupInvestment,
  ScanState,
} from "./types";

const DB_NAME = "florin";
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("roundups")) {
        const store = db.createObjectStore("roundups", { keyPath: "id" });
        store.createIndex("status", "status");
        store.createIndex("timestamp", "timestamp");
      }
      if (!db.objectStoreNames.contains("spends")) {
        const store = db.createObjectStore("spends", { keyPath: "id" });
        store.createIndex("timestamp", "timestamp");
      }
      if (!db.objectStoreNames.contains("kv")) {
        db.createObjectStore("kv");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("tx aborted"));
  });
}

function reqResult<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---------- roundups ----------

export async function putRoundup(r: RoundupInvestment): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("roundups", "readwrite");
  tx.objectStore("roundups").put(r);
  await txDone(tx);
}

export async function getRoundup(id: string): Promise<RoundupInvestment | undefined> {
  const db = await openDb();
  return reqResult(db.transaction("roundups").objectStore("roundups").get(id));
}

export async function allRoundups(): Promise<RoundupInvestment[]> {
  const db = await openDb();
  const all: RoundupInvestment[] = await reqResult(
    db.transaction("roundups").objectStore("roundups").getAll(),
  );
  return all.sort((a, b) => b.timestamp - a.timestamp);
}

/** Insert only if the id is new. Returns true if inserted. Idempotency gate. */
export async function insertRoundupIfNew(r: RoundupInvestment): Promise<boolean> {
  const db = await openDb();
  const tx = db.transaction("roundups", "readwrite");
  const store = tx.objectStore("roundups");
  const existing = await reqResult(store.get(r.id));
  if (existing) {
    await txDone(tx);
    return false;
  }
  store.add(r);
  await txDone(tx);
  return true;
}

// ---------- spends ----------

export async function putSpend(s: DetectedSpend): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("spends", "readwrite");
  tx.objectStore("spends").put({ ...s, id: `${s.txHash}:${s.logIndex}` });
  await txDone(tx);
}

export async function allSpends(): Promise<DetectedSpend[]> {
  const db = await openDb();
  const all: (DetectedSpend & { id: string })[] = await reqResult(
    db.transaction("spends").objectStore("spends").getAll(),
  );
  return all.sort((a, b) => b.timestamp - a.timestamp);
}

// ---------- kv ----------

async function kvGet<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  return reqResult(db.transaction("kv").objectStore("kv").get(key));
}

async function kvSet(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("kv", "readwrite");
  tx.objectStore("kv").put(value, key);
  await txDone(tx);
}

export const DEFAULT_SETTINGS: AppSettings = {
  onboarded: false,
  stockSymbol: "NVDA",
  incrementPence: 100,
  demoMode: true,
  currency: "GBP",
};

export async function getSettings(): Promise<AppSettings> {
  return { ...DEFAULT_SETTINGS, ...((await kvGet<AppSettings>("settings")) ?? {}) };
}

export async function saveSettings(s: AppSettings): Promise<void> {
  await kvSet("settings", s);
}

function scanStateKey(safeAddress: string): string {
  return `scanState:${safeAddress.toLowerCase()}`;
}

export async function getScanState(safeAddress: string): Promise<ScanState | undefined> {
  const scoped = await kvGet<ScanState>(scanStateKey(safeAddress));
  if (scoped) return scoped;

  // One-time migration from the original global watermark. Tag the legacy
  // value with the Safe it was assigned to so it can never be reused if the
  // user later watches another address.
  const legacy = await kvGet<Partial<ScanState> & { lastScannedBlock?: number }>("scanState");
  if (
    legacy?.lastScannedBlock != null &&
    (!legacy.safeAddress || legacy.safeAddress.toLowerCase() === safeAddress.toLowerCase())
  ) {
    const migrated = {
      safeAddress: safeAddress as ScanState["safeAddress"],
      lastScannedBlock: legacy.lastScannedBlock,
    };
    await Promise.all([kvSet(scanStateKey(safeAddress), migrated), kvSet("scanState", migrated)]);
    return migrated;
  }
  return undefined;
}

export async function saveScanState(s: ScanState): Promise<void> {
  await kvSet(scanStateKey(s.safeAddress), s);
}

/** Demo-mode reserve balance in pence (real mode reads the wallet onchain). */
export async function getDemoReservePence(): Promise<number> {
  return (await kvGet<number>("demoReservePence")) ?? 0;
}

export async function setDemoReservePence(pence: number): Promise<void> {
  await kvSet("demoReservePence", pence);
}

export async function getWalletKey(): Promise<string | undefined> {
  return kvGet<string>("walletPrivateKey");
}

export async function setWalletKey(pk: string): Promise<void> {
  await kvSet("walletPrivateKey", pk);
}

/** Gnosis Pay API session (JWT). Cleared on 401 / logout. */
export async function getGpToken(): Promise<string | undefined> {
  return kvGet<string>("gpToken");
}

export async function setGpToken(token: string | undefined): Promise<void> {
  await kvSet("gpToken", token ?? "");
}

function gasBootstrapKey(walletAddress: string): string {
  return `gasBootstrap:${walletAddress.toLowerCase()}`;
}

export async function getGasBootstrapState(
  walletAddress: string,
): Promise<GasBootstrapState | undefined> {
  return kvGet<GasBootstrapState>(gasBootstrapKey(walletAddress));
}

export async function saveGasBootstrapState(state: GasBootstrapState): Promise<void> {
  await kvSet(gasBootstrapKey(state.walletAddress), state);
}

function cardTopUpKey(walletAddress: string): string {
  return `cardTopUp:${walletAddress.toLowerCase()}`;
}

export async function getCardTopUpState(walletAddress: string): Promise<CardTopUpState | undefined> {
  return kvGet<CardTopUpState>(cardTopUpKey(walletAddress));
}

export async function saveCardTopUpState(state: CardTopUpState): Promise<void> {
  await kvSet(cardTopUpKey(state.walletAddress), state);
}

/** Wipe everything (Settings → reset). */
export async function resetDatabase(): Promise<void> {
  const db = await openDb();
  db.close();
  dbPromise = null;
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}
